import { useEffect, useRef, useState } from "react";

/**
 * Full-screen "center your face" cropper for the onboarding face photo.
 * Drag to move, pinch or use the slider to zoom; a face-oval guide shows where
 * the face should go. Confirms with a square JPEG (800x800) blob.
 */
const OUT = 800;
const MAX_ZOOM = 4;
const MIN_ZOOM = 0.5; // zoom out past "fills the square" to fit tight/off-center photos
// How far the photo may slide past the square's edges (fraction of the square),
// so a face near the top/edge of the photo can still be centered. Uncovered
// area is filled with black in the final crop.
const OVERSCROLL = 0.5;
const FILL = "#0b0b0d";

export default function PhotoCropper({ src, onCancel, onConfirm, onLoadError }) {
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const [nat, setNat] = useState(null); // { w, h }
  const [size, setSize] = useState(300); // stage px
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // image top-left inside stage
  const [busy, setBusy] = useState(false);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  // Stage size: a square that fits the phone.
  useEffect(() => {
    const fit = () => setSize(Math.round(Math.min(window.innerWidth - 48, window.innerHeight * 0.5, 380)));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const base = nat ? Math.max(size / nat.w, size / nat.h) : 1; // "cover" scale
  const scale = base * zoom;

  // Let the photo move freely, but never so far that it leaves the square
  // mostly empty: each edge may pass the square's edge by up to OVERSCROLL.
  const clamp = (p, s = scale) => {
    if (!nat) return p;
    const w = nat.w * s, h = nat.h * s;
    const slack = size * OVERSCROLL;
    const minX = Math.min(0, size - w) - slack, maxX = Math.max(0, size - w) + slack;
    const minY = Math.min(0, size - h) - slack, maxY = Math.max(0, size - h) + slack;
    return { x: Math.min(maxX, Math.max(minX, p.x)), y: Math.min(maxY, Math.max(minY, p.y)) };
  };

  // Center the image when it loads / the stage resizes.
  useEffect(() => {
    if (!nat) return;
    setZoom(1);
    setPos({ x: (size - nat.w * base) / 2, y: (size - nat.h * base) / 2 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nat, size]);

  // Zoom around a point (stage coords), keeping that point fixed.
  const zoomTo = (z, cx = size / 2, cy = size / 2) => {
    const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
    const ns = base * nz;
    setPos((p) => {
      const ix = (cx - p.x) / scale, iy = (cy - p.y) / scale; // image point under (cx,cy)
      return clamp({ x: cx - ix * ns, y: cy - iy * ns }, ns);
    });
    setZoom(nz);
  };

  const local = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e) => {
    stageRef.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    const pts = [...pointers.current.values()];
    gesture.current = pts.length >= 2
      ? { kind: "pinch", dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), zoom }
      : { kind: "drag", start: pts[0], pos };
  };
  const onMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, local(e));
    const g = gesture.current;
    const pts = [...pointers.current.values()];
    if (g?.kind === "pinch" && pts.length >= 2) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      zoomTo(g.zoom * (d / (g.dist || 1)), (pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
    } else if (g?.kind === "drag") {
      const p = pts[0];
      setPos(clamp({ x: g.pos.x + (p.x - g.start.x), y: g.pos.y + (p.y - g.start.y) }));
    }
  };
  const onUp = (e) => {
    pointers.current.delete(e.pointerId);
    const pts = [...pointers.current.values()];
    gesture.current = pts.length === 1 ? { kind: "drag", start: pts[0], pos } : null;
  };
  const onWheel = (e) => {
    const p = local(e);
    zoomTo(zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08), p.x, p.y);
  };

  const confirm = async () => {
    if (!nat || busy) return;
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUT;
      canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      // Draw exactly what's on screen (scaled up), black behind any gaps.
      const k = OUT / size;
      ctx.fillStyle = FILL;
      ctx.fillRect(0, 0, OUT, OUT);
      ctx.drawImage(imgRef.current, pos.x * k, pos.y * k, nat.w * scale * k, nat.h * scale * k);
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
      onConfirm(blob);
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="ob-crop" role="dialog" aria-modal="true" aria-label="Crop your photo">
      <div className="ob-crop-inner">
        <h2 className="ob-crop-title">Center your face</h2>
        <p className="ob-crop-sub">Drag to move · pinch or slide to zoom</p>
        <div
          ref={stageRef}
          className="ob-crop-stage"
          style={{ width: size, height: size }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onWheel={onWheel}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            draggable={false}
            onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            onError={() => onLoadError?.()}
            style={nat ? { width: nat.w * scale, height: nat.h * scale, transform: `translate(${pos.x}px, ${pos.y}px)` } : { opacity: 0 }}
          />
          <span className="ob-crop-guide" aria-hidden="true" />
        </div>
        <div className="ob-crop-zoom">
          <span aria-hidden="true">−</span>
          <input type="range" min={MIN_ZOOM} max={MAX_ZOOM} step="0.01" value={zoom} aria-label="Zoom" onChange={(e) => zoomTo(Number(e.target.value))} />
          <span aria-hidden="true">+</span>
        </div>
        <div className="ob-crop-actions">
          <button type="button" className="ob-cta" onClick={confirm} disabled={!nat || busy}>
            {busy ? "Saving…" : "Use photo"}
          </button>
          <button type="button" className="ob-link" onClick={onCancel} disabled={busy}>Choose a different photo</button>
        </div>
      </div>
    </div>
  );
}
